// v10/src/components/ObservationModal.js
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ObservationModal = ({ item, darkMode, onConfirm, onCancel }) => {
  const [comment, setComment] = useState('');
  const textareaRef = useRef(null);

  const itemText = typeof item === 'string' ? item : item?.text || '';
  const isDesktop = window.innerWidth > 640;

  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, []);

  const handleConfirm = () => onConfirm(comment.trim());

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 150,
        display: 'flex',
        alignItems: isDesktop ? 'center' : 'flex-end',
        justifyContent: 'center',
        padding: isDesktop ? '16px' : '0',
        paddingBottom: isDesktop ? '16px' : 'env(safe-area-inset-bottom, 0px)',
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <motion.div
        initial={{ y: isDesktop ? 30 : '100%', opacity: isDesktop ? 0 : 1, scale: isDesktop ? 0.95 : 1 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: isDesktop ? 30 : '100%', opacity: isDesktop ? 0 : 1, scale: isDesktop ? 0.95 : 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{
          width: '100%', maxWidth: '520px',
          borderRadius: isDesktop ? '24px' : '28px 28px 0 0',
          overflow: 'hidden',
          background: darkMode ? 'rgba(15,20,35,0.98)' : 'rgba(255,255,255,0.98)',
          boxShadow: isDesktop ? '0 20px 60px rgba(0,0,0,0.3)' : '0 -8px 50px rgba(0,0,0,0.3)',
          marginBottom: isDesktop ? '0' : '72px',
        }}
      >
        {!isDesktop && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
            <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)' }} />
          </div>
        )}

        <div style={{ padding: isDesktop ? '20px 20px 16px' : '8px 20px 16px', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '12px', flexShrink: 0, background: 'linear-gradient(135deg, #f59e0b, #ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>⚠️</div>
            <div>
              <p style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: darkMode ? 'white' : '#111827' }}>Reportar observación</p>
              <p style={{ margin: 0, fontSize: '11px', color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(107,114,128,1)', fontWeight: 500 }}>El instructor recibirá una notificación</p>
            </div>
          </div>
          <div style={{ padding: '10px 12px', borderRadius: '12px', marginTop: '4px', background: darkMode ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)', border: `1px solid ${darkMode ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.3)'}` }}>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: darkMode ? 'rgba(253,230,138,1)' : '#92400e' }}>📋 {itemText}</p>
          </div>
        </div>

        <div style={{ padding: '16px 20px 20px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: darkMode ? 'rgba(209,213,219,1)' : 'rgba(55,65,81,1)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Descripción de la anomalía <span style={{ color: 'rgba(156,163,175,1)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(opcional)</span>
          </label>
          <textarea
            ref={textareaRef}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Ej: Nivel de aceite en el mínimo, ruido inusual en motor, mancha de aceite bajo el capó..."
            maxLength={300}
            rows={3}
            style={{ width: '100%', resize: 'none', boxSizing: 'border-box', padding: '12px 14px', borderRadius: '14px', border: `1.5px solid ${darkMode ? 'rgba(75,85,99,1)' : 'rgba(209,213,219,1)'}`, background: darkMode ? 'rgba(31,41,55,0.8)' : 'rgba(249,250,251,1)', color: darkMode ? 'rgba(243,244,246,1)' : '#111827', fontSize: '14px', fontFamily: 'inherit', lineHeight: 1.5, outline: 'none' }}
            onFocus={e => e.target.style.borderColor = '#f59e0b'}
            onBlur={e => e.target.style.borderColor = darkMode ? 'rgba(75,85,99,1)' : 'rgba(209,213,219,1)'}
          />
          <p style={{ margin: '4px 0 0', fontSize: '11px', color: darkMode ? 'rgba(107,114,128,1)' : 'rgba(156,163,175,1)', textAlign: 'right' }}>{comment.length}/300</p>

          <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
            <motion.button onClick={onCancel} whileTap={{ scale: 0.96 }}
              style={{ flex: 1, padding: '13px', borderRadius: '14px', border: `1.5px solid ${darkMode ? 'rgba(75,85,99,1)' : 'rgba(209,213,219,1)'}`, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px', fontWeight: 600, color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(107,114,128,1)' }}>
              Cancelar
            </motion.button>
            <motion.button onClick={handleConfirm} whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.02 }}
              style={{ flex: 2, padding: '13px', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg, #f59e0b, #ef4444)', color: 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px', fontWeight: 700, boxShadow: '0 4px 14px rgba(245,158,11,0.4)' }}>
              ⚠️ Confirmar observación
            </motion.button>
          </div>

          <p style={{ margin: '10px 0 0', fontSize: '10.5px', textAlign: 'center', color: darkMode ? 'rgba(107,114,128,1)' : 'rgba(156,163,175,1)' }}>
            Esta observación quedará registrada en el historial de la inspección
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ObservationModal;
